import React, { createContext, useContext, useMemo } from 'react';

import useApi from '../../hooks/useApi';
import { BootResponse, IAuthBootResponse } from '../../constants/apiResponses.types';
import { EBundlesInternalViewModel } from '../../constants/enums';

interface IAuthBootContextValue {
  authBoot: IAuthBootResponse | undefined;
}

const AuthBootContext = createContext<IAuthBootContextValue | undefined>(undefined);

export const resolveAuthBoot = (
  authBootData: IAuthBootResponse | undefined,
  bootData: BootResponse | undefined
): IAuthBootResponse | undefined => {
  if (authBootData) {
    if (authBootData.publisher) return authBootData;
    return {
      ...authBootData,
      publisher: bootData?.storeTheme?.general?.bundlesInternalViewModel || '',
    };
  }

  if (!bootData) return undefined;

  return {
    publisher: bootData.storeTheme?.general?.bundlesInternalViewModel || '',
    storeTheme: {
      login: bootData.storeTheme.login,
      general: bootData.storeTheme.general,
      publisherId: bootData.storeTheme.publisherId,
    },
    integration: bootData.integration,
    featureFlags: bootData.featureFlags,
    language: bootData.language,
    supportConfiguration: bootData.supportConfiguration,
  };
};

export const getStoreBrand = (
  authBoot: IAuthBootResponse | undefined
): EBundlesInternalViewModel | undefined => {
  const publisher = authBoot?.publisher;
  if (!publisher) return undefined;
  return publisher as EBundlesInternalViewModel;
};

export const AuthBootProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const API = useApi({});

  const authBoot = useMemo(
    () => resolveAuthBoot(API.getAuthBoot.data, API.getPublisherMeta.data as BootResponse),
    [API.getAuthBoot.data, API.getPublisherMeta.data]
  );

  return (
    <AuthBootContext.Provider value={{ authBoot }}>{children}</AuthBootContext.Provider>
  );
};

export const useAuthBoot = (): IAuthBootResponse | undefined => {
  const context = useContext(AuthBootContext);
  if (context === undefined) {
    throw new Error('useAuthBoot must be used within an AuthBootProvider');
  }
  return context.authBoot;
};

export const useStoreBrand = (): EBundlesInternalViewModel | undefined => {
  const authBoot = useAuthBoot();
  return getStoreBrand(authBoot);
};
